import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Building2, Copy, Check } from 'lucide-react';

interface OnboardingConfig {
  logo_url?: string;
  font_family?: string;
  primary_color?: string;
  secondary_color?: string;
  // When true, the embed snippet appends ?mode=no_hotel_account so the widget
  // skips the account-creation step and provisions a synthetic user transparently.
  // The hotel team won't get login credentials; the SaaS manages the resulting
  // integration from "Hotels configuration".
  skip_hotel_account?: boolean;
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'system-ui', label: 'System Default' },
];

export function HotelOnboardingConfig() {
  const [config, setConfig] = useState<OnboardingConfig | null>(null);
  const [formData, setFormData] = useState<OnboardingConfig>({
    logo_url: '',
    font_family: 'Inter',
    primary_color: '#8B5CF6',
    secondary_color: '#9D72F7',
    skip_hotel_account: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saasId, setSaasId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      setSaasId(user.id);

      const { data, error: queryError } = await supabase
        .from('saas_details')
        .select('onboarding_config')
        .eq('id', user.id)
        .maybeSingle();

      if (queryError && queryError.code !== 'PGRST116') {
        throw queryError;
      }

      const loadedConfig = (data?.onboarding_config as OnboardingConfig) || null;
      setConfig(loadedConfig);
      setFormData({
        logo_url: loadedConfig?.logo_url || '',
        font_family: loadedConfig?.font_family || 'Inter',
        primary_color: loadedConfig?.primary_color || '#8B5CF6',
        secondary_color: loadedConfig?.secondary_color || '#9D72F7',
        skip_hotel_account: loadedConfig?.skip_hotel_account === true,
      });
    } catch (err) {
      setError('Failed to load onboarding configuration');
      console.error('Error loading config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Validate colors are hex format
      const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (formData.primary_color && !hexColorRegex.test(formData.primary_color)) {
        setError('Primary color must be a valid hex color (e.g., #8B5CF6)');
        return;
      }
      if (formData.secondary_color && !hexColorRegex.test(formData.secondary_color)) {
        setError('Secondary color must be a valid hex color (e.g., #9D72F7)');
        return;
      }

      // Validate logo URL if provided
      if (formData.logo_url && formData.logo_url.trim()) {
        try {
          new URL(formData.logo_url);
        } catch {
          setError('Logo URL must be a valid URL');
          return;
        }
      }

      const configToSave: OnboardingConfig = {
        logo_url: formData.logo_url?.trim() || null,
        font_family: formData.font_family || 'Inter',
        primary_color: formData.primary_color || '#8B5CF6',
        secondary_color: formData.secondary_color || '#9D72F7',
        skip_hotel_account: formData.skip_hotel_account === true,
      };

      const { error: updateError } = await supabase
        .from('saas_details')
        .update({
          onboarding_config: configToSave,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setConfig(configToSave);
    } catch (err) {
      setError('Failed to save onboarding configuration');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  const getEmbedCode = () => {
    if (!saasId) return '';
    const baseUrl = window.location.origin;
    const queryString = formData.skip_hotel_account ? '?mode=no_hotel_account' : '';
    return `<iframe src="${baseUrl}/onboarding/widget/${saasId}${queryString}" width="100%" height="800" frameborder="0" style="border: none;"></iframe>`;
  };

  const copyEmbedCode = async () => {
    const embedCode = getEmbedCode();
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">Loading configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center space-x-2">
          <Building2 className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-700">Hotel Onboarding Configuration</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo URL */}
          <div>
            <label className="form-label">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url || ''}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="form-input"
              placeholder="https://example.com/logo.png"
            />
            <p className="mt-1 text-sm text-gray-500">
              URL to your company logo. Will be displayed at the top of the onboarding widget.
            </p>
          </div>

          {/* Font Family */}
          <div>
            <label className="form-label">Font Family</label>
            <select
              value={formData.font_family || 'Inter'}
              onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
              className="form-input"
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Font family used throughout the onboarding widget.
            </p>
          </div>

          {/* Primary Color */}
          <div>
            <label className="form-label">Primary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.primary_color || '#8B5CF6'}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.primary_color || '#8B5CF6'}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="form-input flex-1"
                placeholder="#8B5CF6"
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Primary brand color used for buttons and highlights.
            </p>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="form-label">Secondary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.secondary_color || '#9D72F7'}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.secondary_color || '#9D72F7'}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="form-input flex-1"
                placeholder="#9D72F7"
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Secondary brand color used for accents and hover states.
            </p>
          </div>

          {/* Preview Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
            <div
              className="p-6 border-2 border-dashed border-gray-300 rounded-lg"
              style={{
                fontFamily: formData.font_family || 'Inter',
                backgroundColor: '#f9fafb',
              }}
            >
              {formData.logo_url && (
                <div className="mb-4 flex justify-center">
                  <img
                    src={formData.logo_url}
                    alt="Logo preview"
                    className="max-h-16 max-w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="text-center space-y-3">
                <h4 className="text-xl font-semibold" style={{ color: formData.primary_color }}>
                  Hotel Onboarding
                </h4>
                <p className="text-gray-600">Welcome to the onboarding process</p>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-white font-medium"
                  style={{ backgroundColor: formData.primary_color }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = formData.secondary_color || formData.primary_color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = formData.primary_color || '#8B5CF6';
                  }}
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>

          {/* No Hotel Account Mode Toggle */}
          {saasId && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Onboarding mode</h3>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.skip_hotel_account === true}
                  onChange={(e) =>
                    setFormData({ ...formData, skip_hotel_account: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    Skip hotel account creation (frictionless mode)
                  </span>
                  <span className="block mt-1 text-sm text-gray-500">
                    The widget skips the account-creation step. A synthetic user is
                    provisioned transparently when the hotel submits its details, and
                    the resulting integration is manageable from your{' '}
                    <strong>Hotels configuration</strong> section. The hotel does not
                    receive login credentials.
                  </span>
                </span>
              </label>
              <p className="mt-3 text-xs text-gray-500">
                Toggling this here automatically appends <code className="px-1 py-0.5 bg-gray-100 rounded">?mode=no_hotel_account</code> to the embed snippet below. Save the configuration to persist it.
              </p>
            </div>
          )}

          {/* Embed Code Section */}
          {saasId && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Embed Code</h3>
              <p className="text-sm text-gray-500 mb-3">
                Copy this code to embed the onboarding widget on your website:
              </p>
              <div className="relative">
                <textarea
                  readOnly
                  value={getEmbedCode()}
                  className="form-input font-mono text-sm h-24"
                  onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                />
                <button
                  type="button"
                  onClick={copyEmbedCode}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700"
                  title="Copy embed code"
                >
                  {copied ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <Copy className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
